import { plural } from "../text";

/**
 * How a fact value reads in the UI, declared with its retriever. `true` / `false`
 * for booleans, `zero` for 0, `values` for known strings, and `text` for the
 * rest: `{value}` is the value, `{value:unit}` a count of units ("3 days").
 * @public
 */
export interface FactFormat {
  true?: string;
  false?: string;
  zero?: string;
  text?: string;
  values?: Record<string, string>;
}

/** One declared fact. @public */
export interface DeclaredFact {
  type: string;
  description: string;
  format?: FactFormat;
}

/** Why a check reading this retriever's facts may have no data. @public */
export interface FactSourceHints {
  /** Short name of the source, e.g. GitHub. */
  label: string;
  /** For a category header when nothing in it has data. */
  noDataShort: string;
  /** No fact row at all for this retriever and entity. */
  noRow: string;
  /** A row exists but lacks a fact the check reads (jsonfc then skips the check). */
  missingFact: string;
}

/** One retriever's declared facts, as the frontend sees it. @public */
export interface DeclaredRetriever {
  id: string;
  hints: FactSourceHints;
  facts: Record<string, DeclaredFact>;
}

/** Body of `GET /api/front-door/facts`. @public */
export interface FactsResponse {
  retrievers: DeclaredRetriever[];
}

/** @public */
export const FRONT_DOOR_FACTS_PATH = "/facts";

/** Hints for a retriever that declares none. @public */
export const UNKNOWN_FACT_SOURCE: FactSourceHints = Object.freeze({
  label: "Unknown source",
  noDataShort: "no data yet",
  noRow: "No facts collected for this check yet.",
  missingFact: "The facts this check needs are missing.",
});

const PLACEHOLDER = /\{value(?::([^}]+))?\}/g;

function fill(text: string, value: string | number): string {
  return text.replace(PLACEHOLDER, (_match, unit?: string) => {
    if (typeof value !== "number") return value;
    return unit ? plural(value, unit) : value.toLocaleString("en-US");
  });
}

/** The value in the format's words; undefined when the format does not cover it. @public */
export function formatFactValue(
  format: FactFormat,
  value: unknown
): string | undefined {
  if (typeof value === "boolean") return value ? format.true : format.false;
  if (typeof value === "number") {
    if (value === 0 && format.zero !== undefined) return format.zero;
    return format.text === undefined ? undefined : fill(format.text, value);
  }
  if (typeof value === "string") {
    const named = format.values?.[value];
    if (named !== undefined) return named;
    return format.text === undefined ? undefined : fill(format.text, value);
  }
  return undefined;
}

/** Every declared fact by name, with the retriever that declares it. @public */
export function indexDeclaredFacts(
  retrievers: ReadonlyArray<DeclaredRetriever>
): Map<string, DeclaredFact & { retrieverId: string }> {
  const index = new Map<string, DeclaredFact & { retrieverId: string }>();
  for (const retriever of retrievers) {
    for (const [name, fact] of Object.entries(retriever.facts)) {
      index.set(name, { retrieverId: retriever.id, ...fact });
    }
  }
  return index;
}

/** The hints for a retriever id, or UNKNOWN_FACT_SOURCE. @public */
export function factSourceHints(
  retrievers: ReadonlyArray<DeclaredRetriever> | undefined,
  retrieverId: string
): FactSourceHints {
  return (
    retrievers?.find((r) => r.id === retrieverId)?.hints ?? UNKNOWN_FACT_SOURCE
  );
}
